import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components';
import type { VideoAspectRatio } from '@/store/slices/creativeJobs';
import { aspectRatioValue } from '../../aspect-ratios';
import { downloadAsset } from '../../download-asset';
import {
  Frame,
  Footer,
  PlayBadge,
  PosterImg,
  PosterVideo,
  PreviewButton,
  ReadyText,
  Root,
  Video,
} from './VideoResult.styles';

export type VideoResultProps = {
  /** The finished clip's URL (a CDN-hosted mp4/webm). */
  url: string;
  /** The filename the download saves as (built by the caller via `assetFilename`). */
  filename: string;
  /** The clip's aspect ratio, so the frame matches what was generated. */
  aspect: VideoAspectRatio;
  /** A still shown under a play badge until the viewer clicks to play; falls back to the clip's own
   *  first frame when absent. */
  poster?: string;
};

/**
 * Playback for a finished video generation: a poster thumbnail with a play badge that swaps to the
 * clip (native controls, autoplay) on click, a "ready" line, and a Download button. Fetches the clip
 * as a blob so it saves as a real file (falling back to opening it in a tab if the CDN blocks the
 * cross-origin fetch).
 */
export const VideoResult = ({ url, filename, aspect, poster }: VideoResultProps): React.ReactElement => {
  const { t } = useTranslation();
  const [playing, setPlaying] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Start playback WITH SOUND when the viewer clicks the poster. The `autoplay` attribute is muted or blocked
  // by the browser's autoplay policy (silent playback, the exact bug reported); calling play() imperatively in
  // the click-driven mount keeps the user's activation, so the audio plays. muted=false is defensive.
  React.useEffect(() => {
    if (!playing) return;
    const el = videoRef.current;
    if (!el) return;
    el.muted = false;
    void el.play().catch(() => undefined);
  }, [playing]);

  return (
    <Root>
      <Frame $ratio={aspectRatioValue(aspect)}>
        {playing ? (
          <Video ref={videoRef} controls playsInline preload="metadata" src={url} />
        ) : (
          <PreviewButton aria-label={t('Play video')} onClick={() => setPlaying(true)}>
            {poster ? (
              <PosterImg src={poster} alt="" />
            ) : (
              <PosterVideo src={url} preload="metadata" muted playsInline />
            )}
            <PlayBadge aria-hidden="true" />
          </PreviewButton>
        )}
      </Frame>
      <Footer>
        <ReadyText text="Your video is ready." />
        <Button
          variant="primary"
          icon="download"
          label={t('Download')}
          onClick={() => void downloadAsset(url, filename)}
        />
      </Footer>
    </Root>
  );
};

export default VideoResult;
